import { useState, useEffect, useLayoutEffect } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { selectLoginLanguage, applyLoginDefaultLanguage } from '../../i18n';
import { forceLightTheme, restoreThemeFromPref } from '../../services/theme';
import { useAuth } from '../../context/AuthContext';
import { useInstallFlow } from '../../hooks/useInstallFlow';
import { InstallInstructions } from '../../components/InstallInstructions/InstallInstructions';
import './LoginPage.css';

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const install = useInstallFlow();

  // El login arranca en inglés por defecto (salvo preferencia manual de Ajustes).
  useEffect(() => { applyLoginDefaultLanguage(); }, []);

  // El login SIEMPRE en claro, aunque el usuario tenga el tema oscuro. Al salir
  // (tras iniciar sesión) se restaura su preferencia. useLayoutEffect para
  // aplicarlo antes del primer paint y evitar el parpadeo al cerrar sesión.
  useLayoutEffect(() => {
    forceLightTheme();
    return () => { restoreThemeFromPref(); };
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/positions', { replace: true });
    } catch {
      setError(t('login.error'));
    } finally {
      setLoading(false);
    }
  };

  const currentLang = i18n.language?.startsWith('es') ? 'es' : 'en';

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo-wrap">
          <img src="/logo.svg" alt="Aldahra Logistics" className="login-logo" />
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="login-field">
            <label htmlFor="email">{t('login.email')}</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('login.emailPlaceholder')}
              required
            />
          </div>

          <div className="login-field">
            <label htmlFor="password">{t('login.password')}</label>
            <div className="login-password-wrap">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                className="login-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
                aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46A11.804 11.804 0 0 0 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78 3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && <p className="login-error">{error}</p>}

          <button
            type="submit"
            className="login-btn"
            disabled={!email || !password || loading}
          >
            {loading ? t('login.signingIn') : t('login.signIn')}
          </button>
        </form>

        <a
          className="login-forgot"
          href="https://aldahramanagementusers.azurewebsites.net/Identity/Account/ForgotPassword"
          target="_blank"
          rel="noreferrer"
        >
          {t('login.forgot')}
        </a>

        {!install.hidden && (
          <button type="button" className="login-install" onClick={install.trigger}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden="true">
              <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
            </svg>
            {t('components.install.installApp')}
          </button>
        )}

        <p className="login-footer">{t('login.developedBy')} <a href="https://drpinocode.com" target="_blank" rel="noreferrer">drpinocode.com</a></p>

        <p className="login-meta">
          <a href="https://portal.drpinocode.com/privacy-policy-aldahra-europe" target="_blank" rel="noreferrer">{t('settings.privacy')}</a>
          <span className="login-meta__sep">·</span>
          <span>v{__APP_VERSION__}</span>
        </p>

        <div className="login-lang" role="group" aria-label="Language">
          {(['en', 'es'] as const).map((lng) => (
            <button
              key={lng}
              type="button"
              className={`login-lang__btn${currentLang === lng ? ' login-lang__btn--active' : ''}`}
              onClick={() => selectLoginLanguage(lng)}
            >
              {lng.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <InstallInstructions open={install.modalOpen} onClose={install.closeModal} />
    </div>
  );
}
